/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import React from 'react';
import { observer } from 'mobx-react';
import { Route } from 'react-router';
import AppTiles from './appTiles';
import Banners from './banners';
import Empty from './empty';
import Fail from './connectionFail';
import { ANALYTICS } from '../../../../utils/enums';
import { CatalogAnalytics, analyticsSharedData } from '../../../../analytics';
import PageLoader from '../../../../components/pageLoader';
import CCXUIAppConfig from '../../../../stores/config';


let routingPath = '';
let scrollPos = 0;

const sendPageRenderAnalytics = (
    appConfig, appsSkeletonModel, subSectionList, routingProps, appSection,
) => {
    // this checks to skip multiple render of apps/all/desktop, and apps/all
    // need to remove once the issue is fixed. - Sunil
    let subSectionInUrl = true;
    if (subSectionList.length) {
        subSectionInUrl = false;
        const path = routingProps.location.pathname.split('/');
        if (path.length > 3) {
            subSectionList.forEach((subSection) => {
                subSectionInUrl = subSectionInUrl || subSection.id === path[3];
            });
        }
    } else {
        appsSkeletonModel.setSelectedSubSection();
    }
    if (routingProps.location && routingPath !== routingProps.location.pathname
      && subSectionInUrl) {
        routingPath = routingProps.location.pathname;
        CatalogAnalytics.sendAnalytics(appConfig, appsSkeletonModel, ANALYTICS.EVENTS.RENDER_PAGE,
            { routingProps, appSection },
            analyticsSharedData.shouldSendLoadTime);
        analyticsSharedData.shouldSendLoadTime = false;
    }
};

const wrapContent = (bannerContent, appsContent, props) => (
    <div
        onScroll={(e) => {
            if (e.target.scrollTop !== scrollPos) {
                scrollPos = e.target.scrollTop;
            } else {
                e.stopPropagation();
            }
        }}
        // className="apps-section-content"
        className={CCXUIAppConfig.noBanner ? 'apps-section-content no-banner-on-mobile' : 'apps-section-content'}
        {...props}>
        <div className={CCXUIAppConfig.shortHeight ? 'apps-banner dynamic-banner-on-mobile' : 'apps-banner'}>
            {bannerContent}
        </div>
        {appsContent}
    </div>
);

export default observer(({
    appLoader, appConfig, appsSkeletonModel,
    allAppsModel, bannersModel, appSection, ...routingProps
}) => {
    const { layouts } = appConfig;
    const { match } = routingProps;
    const { catalogLoading } = appLoader;
    const {
        subSectionList = [], emptyState, failState, showWait,
    } = appSection;
    const isEmpty = !allAppsModel.existApps({
        appSectionFilter: appSection.id,
        isHidden: false,
    });
    sendPageRenderAnalytics(appConfig, appsSkeletonModel, subSectionList, routingProps, appSection);
    if (catalogLoading || showWait) {
        return <PageLoader />;
    }
    if (failState && failState.status) {
        return <Fail failState={failState} appConfig={appConfig} appSection={appSection} />;
    }
    if (isEmpty && emptyState) {
        return <Empty emptyState={emptyState} />;
    }
    if (subSectionList.length === 0) {
        return wrapContent(
            <Banners
                bannersModel={bannersModel}
                appSection={appSection}
                bannerLayouts={layouts}
                appConfig={appConfig}
                appsSkeletonModel={appsSkeletonModel}
                allAppsModel={allAppsModel}
                {...routingProps} />,
            <AppTiles
                appConfig={appConfig}
                appsSkeletonModel={appsSkeletonModel}
                allAppsModel={allAppsModel}
                appSection={appSection}
                layouts={layouts}
                subSection={{}}
                {...routingProps} />
        );
    }
    return subSectionList.map(subSection => (
        <Route
            key={subSection.id}
            path={`${match.path}/${subSection.id}`}
            render={() => wrapContent(
                <Banners
                    appConfig={appConfig}
                    appsSkeletonModel={appsSkeletonModel}
                    bannersModel={bannersModel}
                    appSection={appSection}
                    subSection={subSection}
                    allAppsModel={allAppsModel}
                    bannerLayouts={layouts}
                    {...routingProps} />,
                <AppTiles
                    appConfig={appConfig}
                    appsSkeletonModel={appsSkeletonModel}
                    allAppsModel={allAppsModel}
                    appSection={appSection}
                    subSection={subSection}
                    layouts={layouts}
                    {...routingProps} />,
                {
                    role: 'tabpanel',
                    id: `${subSection.id}-tabpanel`,
                    'aria-labelledby': `${subSection.id}-tab`,
                }
            )} />
    ));
});
